import React, { useState, useEffect } from "react";
import { Timeline, Tag, Alert, Spin, Space, Tooltip, Button } from "antd";
import { MailOutlined, EditOutlined, SyncOutlined, ClockCircleOutlined, ReloadOutlined, EyeOutlined } from "@ant-design/icons";
import { scheduleApi } from "../../../../api/scheduleApi";
import { getScheduleEmailContent } from "../../../../api/mailApi";
import EmailViewer from "../../../../components/common/EmailViewer";
import dayjs from "dayjs";

// 活动类型映射 - 统一的活动类型定义
const ACTIVITY_TYPE_MAP = {
    // 状态变更
    STATUS_CHANGE: {
        icon: <EditOutlined />,
        color: "blue",
        text: "状态变更",
    },

    // 邮件活动
    EMAIL_CONFIRMATION_SENT: {
        icon: <MailOutlined />,
        color: "green",
        text: "确认邮件发送成功",
    },
    EMAIL_CONFIRMATION_FAILED: {
        icon: <MailOutlined />,
        color: "red",
        text: "确认邮件发送失败",
    },
    EMAIL_PUBLISH_SENT: {
        icon: <MailOutlined />,
        color: "cyan",
        text: "发布邮件发送成功",
    },
    EMAIL_PUBLISH_FAILED: {
        icon: <MailOutlined />,
        color: "red",
        text: "发布邮件发送失败",
    },
    EMAIL_BULK_SENT: {
        icon: <MailOutlined />,
        color: "purple",
        text: "群发邮件发送成功",
    },
    EMAIL_BULK_FAILED: {
        icon: <MailOutlined />,
        color: "red",
        text: "群发邮件发送失败",
    },

    // 计划操作
    SCHEDULE_PUBLISHED: {
        icon: <SyncOutlined />,
        color: "purple",
        text: "计划发布",
    },
    SCHEDULE_UNPUBLISHED: {
        icon: <SyncOutlined />,
        color: "orange",
        text: "取消发布",
    },
};

// 操作者类型映射
const OPERATOR_TYPE_MAP = {
    USER: { text: "用户", color: "blue" },
    SYSTEM: { text: "系统", color: "green" },
    AI_AGENT: { text: "AI", color: "purple" },
    API_CLIENT: { text: "API", color: "orange" },
};

/**
 * 计划活动时间线组件（完全重构版）
 * 显示最近的状态变更、邮件发送等活动
 */
const ScheduleActivityTimeline = ({ scheduleId }) => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 邮件查看器状态
    const [emailViewerVisible, setEmailViewerVisible] = useState(false);
    const [selectedEmailData, setSelectedEmailData] = useState(null);

    // 加载活动时间线数据
    const loadActivities = async () => {
        if (!scheduleId) {
            console.log("[ScheduleActivityTimeline] scheduleId为空，跳过加载");
            return;
        }

        try {
            setLoading(true);
            setError(null);

            console.log("[ScheduleActivityTimeline] 开始加载活动时间线，scheduleId:", scheduleId);

            const response = await scheduleApi.getScheduleActivityTimeline(scheduleId, {
                page: 1,
                page_size: 50,
            });

            console.log("[ScheduleActivityTimeline] API响应:", response);

            const activitiesData = response.data?.items || [];
            console.log("[ScheduleActivityTimeline] 解析到的活动数据:", activitiesData);

            setActivities(activitiesData);
        } catch (err) {
            console.error("加载活动时间线失败:", err);
            setError(`加载活动时间线失败: ${err.message || err}`);
        } finally {
            setLoading(false);
        }
    };

    // 初始加载
    useEffect(() => {
        loadActivities();
    }, [scheduleId]);

    // 处理查看邮件
    const handleViewEmail = async (activity) => {
        console.log("[ScheduleActivityTimeline] 查看邮件:", activity);

        // 从新的数据结构中获取邮件ID
        const emailId = activity.email_data?.email_id;

        if (!emailId || !scheduleId) {
            console.error("[ScheduleActivityTimeline] 缺少邮件ID或计划ID", { emailId, scheduleId });
            return;
        }

        // 判断是否为群发邮件
        const isBulkEmail = activity.type === "EMAIL_BULK_SENT" || activity.type === "EMAIL_BULK_FAILED";

        try {
            console.log("[ScheduleActivityTimeline] 开始获取邮件详细内容:", { scheduleId, emailId, isBulkEmail });

            let emailDetail = null;

            // 使用统一的邮件API获取内容，符合重构设计原则
            console.log("[ScheduleActivityTimeline] 使用统一邮件API获取邮件内容");
            try {
                const response = await getScheduleEmailContent(scheduleId, emailId);
                emailDetail = response?.data;
            } catch (contentError) {
                console.warn("[ScheduleActivityTimeline] 获取邮件内容失败，使用活动数据:", contentError);
                // 如果获取邮件内容失败，使用活动数据构造基本信息
                emailDetail = {
                    subject: activity.email_data?.email_subject,
                    to_address: activity.email_data?.recipients_to,
                    cc_address: activity.email_data?.recipients_cc,
                    bcc_address: activity.email_data?.recipients_bcc,
                    sent_at: activity.timestamp,
                    status: activity.email_data?.email_status,
                    body_html: "无法获取邮件内容",
                    link_type: activity.email_data?.email_type || activity.email_data?.business_link?.link_type,
                };
            }

            if (!emailDetail) {
                console.error("[ScheduleActivityTimeline] 未获取到邮件详细内容");
                return;
            }

            console.log("[ScheduleActivityTimeline] 获取到邮件详细内容:", emailDetail);

            // 构造邮件数据对象，适配EmailViewer组件
            const emailData = {
                subject: emailDetail.subject || "无主题",
                email_subject: emailDetail.subject,
                email_type: emailDetail.business_link?.link_type || emailDetail.link_type || activity.email_data?.email_type,
                recipients: emailDetail.to_address || activity.email_data?.recipients_to,
                to: emailDetail.to_address,
                cc: emailDetail.cc_address || activity.email_data?.recipients_cc,
                bcc: emailDetail.bcc_address || activity.email_data?.recipients_bcc,
                send_time: emailDetail.sent_at || emailDetail.date || activity.timestamp,
                status: activity.email_data?.email_status || emailDetail.status,
                content_html: emailDetail.body_html,
                rendered_html: emailDetail.body_html,
                content: emailDetail.body_text,
                attachments: [],
                error: activity.email_data?.error_message,

                // 统计信息（优先使用邮件详情中的统计，回退到活动数据）
                total_recipients: emailDetail.total_recipients || activity.email_data?.total_recipients,
                sent_count: emailDetail.sent_count || activity.email_data?.sent_count,
                failed_count: emailDetail.failed_count || activity.email_data?.failed_count,

                // 业务上下文
                business_context: emailDetail.business_link || activity.email_data?.business_context,

                // 群发邮件标识和相关信息
                is_bulk_email: emailDetail.is_bulk_email || isBulkEmail,
                related_email_id: emailId,
            };

            console.log("[ScheduleActivityTimeline] 构造的邮件数据:", emailData);
            setSelectedEmailData(emailData);
            setEmailViewerVisible(true);
        } catch (error) {
            console.error("[ScheduleActivityTimeline] 获取邮件详细内容失败:", error);

            // 如果获取详细内容失败，使用活动数据中的基本信息
            const emailData = {
                subject: activity.email_data?.email_subject || "无主题",
                email_subject: activity.email_data?.email_subject,
                email_type: activity.email_data?.email_type,
                recipients: activity.email_data?.recipients_to,
                to: activity.email_data?.recipients_to,
                cc: activity.email_data?.recipients_cc,
                send_time: activity.timestamp,
                status: activity.email_data?.email_status || "unknown",
                content_html: "邮件内容加载失败",
                rendered_html: "邮件内容加载失败",
                content: "邮件内容加载失败",
                attachments: [],
                error: activity.email_data?.error_message || error.message,

                // 群发邮件标识
                is_bulk_email: isBulkEmail,
                related_email_id: emailId,
            };

            setSelectedEmailData(emailData);
            setEmailViewerVisible(true);
        }
    };

    // 关闭邮件查看器
    const handleCloseEmailViewer = () => {
        setEmailViewerVisible(false);
        setSelectedEmailData(null);
    };

    // 渲染时间线项内容
    const renderTimelineItem = (activity) => {
        const { type } = activity;
        const activityConfig = ACTIVITY_TYPE_MAP[type] || {
            icon: <ClockCircleOutlined />,
            color: "default",
            text: type,
        };

        // 根据活动类型渲染不同内容
        switch (type) {
            case "STATUS_CHANGE":
                return {
                    dot: activityConfig.icon,
                    color: activityConfig.color,
                    children: (
                        <div>
                            <div style={{ marginBottom: 4 }}>
                                <Space>
                                    <Tag color={activityConfig.color}>{activityConfig.text}</Tag>
                                    {activity.status_data?.old_status !== activity.status_data?.new_status ? (
                                        <Space>
                                            <Tag color="orange">{activity.status_data?.old_status}</Tag>
                                            <span>→</span>
                                            <Tag color="green">{activity.status_data?.new_status}</Tag>
                                        </Space>
                                    ) : (
                                        <Tag color="default">{activity.status_data?.new_status}</Tag>
                                    )}
                                </Space>
                            </div>
                            {activity.status_data?.change_reason && <div style={{ color: "#666", fontSize: "12px", marginBottom: 4 }}>原因：{activity.status_data.change_reason}</div>}
                            <div style={{ fontSize: "12px", color: "#999" }}>
                                <Space>
                                    <span>{dayjs(activity.timestamp).format("MM-DD HH:mm:ss")}</span>
                                    {activity.operator_name && (
                                        <Tag size="small" color={OPERATOR_TYPE_MAP[activity.operator_type]?.color || "default"}>
                                            {activity.operator_name}
                                        </Tag>
                                    )}
                                </Space>
                            </div>
                        </div>
                    ),
                };

            case "EMAIL_CONFIRMATION_SENT":
            case "EMAIL_CONFIRMATION_FAILED":
            case "EMAIL_PUBLISH_SENT":
            case "EMAIL_PUBLISH_FAILED":
            case "EMAIL_BULK_SENT":
            case "EMAIL_BULK_FAILED":
                return {
                    dot: activityConfig.icon,
                    color: activityConfig.color,
                    children: (
                        <div>
                            <div style={{ marginBottom: 4 }}>
                                <Space>
                                    <Tag color={activityConfig.color}>{activityConfig.text}</Tag>
                                    {activity.email_data?.email_type && <Tag color="blue">{activity.email_data.email_type}</Tag>}
                                    {activity.email_data?.email_id && (
                                        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewEmail(activity)} style={{ padding: "0 4px", height: "auto" }}>
                                            查看邮件
                                        </Button>
                                    )}
                                </Space>
                            </div>
                            {activity.email_data?.email_subject && (
                                <div style={{ fontWeight: "500", marginBottom: 4 }}>
                                    <Tooltip title="点击查看完整邮件内容">
                                        <span style={{ cursor: "pointer", color: "#1890ff" }} onClick={() => handleViewEmail(activity)}>
                                            {activity.email_data.email_subject}
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                            {activity.email_data?.recipients_to && <div style={{ color: "#666", fontSize: "12px", marginBottom: 4 }}>收件人：{activity.email_data.recipients_to}</div>}
                            {activity.email_data?.recipients_cc && <div style={{ color: "#666", fontSize: "12px", marginBottom: 4 }}>抄送：{activity.email_data.recipients_cc}</div>}

                            {/* 邮件发送统计信息 */}
                            {(activity.email_data?.total_recipients > 0 || activity.email_data?.sent_count > 0 || activity.email_data?.failed_count > 0) && (
                                <div style={{ color: "#666", fontSize: "12px", marginBottom: 4 }}>
                                    {activity.email_data.total_recipients > 0 && <span>总收件人：{activity.email_data.total_recipients}</span>}
                                    {activity.email_data.sent_count > 0 && <span style={{ marginLeft: 8, color: "#52c41a" }}>成功：{activity.email_data.sent_count}</span>}
                                    {activity.email_data.failed_count > 0 && <span style={{ marginLeft: 8, color: "#ff4d4f" }}>失败：{activity.email_data.failed_count}</span>}
                                </div>
                            )}

                            {/* 错误信息显示 */}
                            {activity.email_data?.error_message && <div style={{ color: "#ff4d4f", fontSize: "12px", marginBottom: 4 }}>错误：{activity.email_data.error_message}</div>}

                            <div style={{ fontSize: "12px", color: "#999" }}>
                                <Space>
                                    <span>{dayjs(activity.timestamp).format("MM-DD HH:mm:ss")}</span>
                                    {activity.operator_name && (
                                        <Tag size="small" color={OPERATOR_TYPE_MAP[activity.operator_type]?.color || "default"}>
                                            {activity.operator_name}
                                        </Tag>
                                    )}
                                </Space>
                            </div>
                        </div>
                    ),
                };

            case "SCHEDULE_PUBLISHED":
            case "SCHEDULE_UNPUBLISHED":
                return {
                    dot: activityConfig.icon,
                    color: activityConfig.color,
                    children: (
                        <div>
                            <div style={{ marginBottom: 4 }}>
                                <Space>
                                    <Tag color={activityConfig.color}>{activityConfig.text}</Tag>
                                    <Tag color="default">{activity.status_data?.new_status}</Tag>
                                </Space>
                            </div>
                            {activity.status_data?.change_reason && <div style={{ color: "#666", fontSize: "12px", marginBottom: 4 }}>{activity.status_data.change_reason}</div>}
                            <div style={{ fontSize: "12px", color: "#999" }}>
                                <Space>
                                    <span>{dayjs(activity.timestamp).format("MM-DD HH:mm:ss")}</span>
                                    {activity.operator_name && (
                                        <Tag size="small" color={OPERATOR_TYPE_MAP[activity.operator_type]?.color || "default"}>
                                            {activity.operator_name}
                                        </Tag>
                                    )}
                                </Space>
                            </div>
                        </div>
                    ),
                };

            default:
                return {
                    dot: <ClockCircleOutlined />,
                    color: "default",
                    children: (
                        <div>
                            <div style={{ marginBottom: 4 }}>
                                <Tag color="default">{type}</Tag>
                            </div>
                            <div style={{ fontSize: "12px", color: "#999" }}>{dayjs(activity.timestamp).format("MM-DD HH:mm:ss")}</div>
                        </div>
                    ),
                };
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin size="large" />
                <div style={{ marginTop: 8 }}>加载活动记录中...</div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="加载失败"
                description={error}
                type="error"
                showIcon
                action={
                    <Button size="small" onClick={loadActivities}>
                        重试
                    </Button>
                }
            />
        );
    }

    if (activities.length === 0) {
        return (
            <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                <ClockCircleOutlined style={{ fontSize: "24px", marginBottom: "8px" }} />
                <div>暂无活动记录</div>
                <div style={{ fontSize: "12px", marginTop: "8px" }}>该计划还没有状态变更、邮件发送等活动记录</div>
                <Button type="link" size="small" onClick={loadActivities} style={{ marginTop: "8px" }}>
                    重新加载
                </Button>
            </div>
        );
    }

    return (
        <>
            <div className="schedule-activity-timeline">
                {/* 工具栏 */}
                <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ color: "#666", fontSize: "14px" }}>
                        显示最近 {activities.length} 条活动记录
                        {scheduleId && <span style={{ marginLeft: 8, fontSize: "12px" }}>(计划ID: {scheduleId})</span>}
                    </div>
                    <Button type="text" icon={<ReloadOutlined />} onClick={loadActivities} loading={loading}>
                        刷新
                    </Button>
                </div>

                {/* 时间线 */}
                <Timeline mode="left" items={activities.map((activity) => renderTimelineItem(activity))} />
            </div>

            {/* 邮件查看器 */}
            <EmailViewer visible={emailViewerVisible} onClose={handleCloseEmailViewer} emailData={selectedEmailData} title="邮件详情" width={900} showActions={false} />
        </>
    );
};

export default ScheduleActivityTimeline;
